import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08; // brief white flinch flash on taking damage

/**
 * A minion with no ranged attack that's immune to damage while its
 * `linkedMinion` (assigned once, at wave-spawn time — see
 * MinionWaveSystem._linkWardens) is still alive. Forces an actual kill-order
 * decision instead of "shoot whatever's closest": ignore the link and keep
 * hitting the Warden for nothing, or kill the link first and open it up.
 * Same public shape as Skirmisher/Charger/Bomber (x/y/radius/hp/isDead/
 * takeDamage/update) so it drops into CombatSystem's enemy registry and
 * MinionWaveSystem's pool without either needing to know the difference —
 * CombatSystem.getNearestTarget/_checkCollisions/_applyDashAoe read
 * `invulnerable` generically off any enemy, not just the boss.
 */
export class Warden {
  constructor(eventBus, { x, y, radius = 6, hp, moveSpeed, contactDamage }) {
    this.eventBus = eventBus;
    this.type = 'warden';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;

    // Set post-construction by MinionWaveSystem once the whole wave has
    // spawned, so it can link to a sibling minion instead of itself.
    this.linkedMinion = null;
  }

  /** Read by CombatSystem (targeting/collision) and Game.js (render tint). */
  get invulnerable() {
    return !!this.linkedMinion && !this.linkedMinion.isDead;
  }

  update(dt, player, _bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;

    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  // Defense-in-depth — see Skirmisher._clampToArena / Charger's charge-duration cap.
  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  /** Shield blocks damage entirely rather than reducing it — same early-return shape as Boss.takeDamage. */
  takeDamage(amount) {
    if (this.isDead || this.invulnerable) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
